import React, { useState, useContext } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { Headline } from "../../components/Headline";
import FormField from "../../components/form-components/FormField";

import t from "../../lib/translate";
import FormService from "../../lib/service/formService";
import { FormSent } from "../FormSent/FormSent";
import FormLayoutContext from "../../layouts/StandardFormLayout/context";
import { resolveSessionValues } from "../resolveSessionValues";
import FocusError from "../FocusError";

function TicketOrderForm({ formId, locale, locationState = {}, isSending }) {
    const [isSent, setIsSent] = useState(false);
    const { setShowSubmit } = useContext(FormLayoutContext);

    const service = new FormService(
        process.env.GATSBY_FORM_MICROSERVICE_BASE_URL,
        locale
    );

    const submit = (userData) => {
        const formData = {
            ...userData,
            language: locale,
            url: locationState?.prevUrl ?? "",
            gdpr: userData.gdpr ? "1" : "0",
            newsletter: userData.newsletter ? "1" : "0"
        };

        const { campaign, pnr, dnr } = resolveSessionValues();

        if (campaign) {
            formData.campaign = campaign.name;
        }

        if (pnr) {
            formData.pnr = pnr;
        }

        if (dnr) {
            formData.dnr = dnr;
        }

        isSending(true);
        service
            .submit("ticket-order-form", formData)
            .then((res) => {
                if (res === null) {
                    window?.dataLayer?.push({
                        event: "form_successfully_submitted"
                    });
                    isSending(false);
                    setIsSent(true);
                    setShowSubmit(false);
                }
            })
            .catch((error) => {
                console.error(error);
                isSending(false);
            });
    };

    return (
        <>
            {!isSent && (
                <Formik
                    initialValues={{
                        email: "",
                        salutation: "",
                        title: "",
                        firstname: "",
                        lastname: "",
                        company: "",
                        country: "",
                        zip: "",
                        city: "",
                        street: "",
                        number: "",
                        phone: "",
                        cardnumber: "",
                        message: "",
                        newsletter: "",
                        gdpr: ""
                    }}
                    validationSchema={Yup.object({
                        email: Yup.string()
                            .email()
                            .required(t("form.ticket.email.error")),
                        salutation: Yup.string()
                            .required(t("form.ticket.salutation.error"))
                            .oneOf([
                                t("form.ticket.salutation.value-01"),
                                t("form.ticket.salutation.value-02"),
                                t("form.ticket.salutation.value-03"),
                                t("form.ticket.salutation.value-04")
                            ]),
                        title: Yup.string(),
                        firstname: Yup.string().required(
                            t("form.ticket.firstname.error")
                        ),
                        lastname: Yup.string().required(
                            t("form.ticket.surname.error")
                        ),
                        company: Yup.string().required(
                            t("form.ticket.company.error")
                        ),
                        country: Yup.string(),
                        zip: Yup.string().required(
                            t("form.ticket.zipcode.error")
                        ),
                        city: Yup.string().required(
                            t("form.ticket.city.error")
                        ),
                        street: Yup.string(),
                        number: Yup.string(),
                        phone: Yup.number().required(
                            t("form.ticket.phone.error")
                        ),
                        cardnumber: Yup.number().required(
                            t("form.ticket.cardnumber.error")
                        ),
                        message: Yup.string(),
                        newsletter: Yup.boolean().oneOf([true, false]),
                        gdpr: Yup.boolean()
                            .oneOf([true], t("form.ticket.privacypolicy.error"))
                            .required(t("form.ticket.privacypolicy.error"))
                    })}
                    onSubmit={submit}
                >
                    <Form id={formId}>
                        <legend>
                            <Headline semanticOfHeadline="H1">
                                {t("form.ticket.headline")}
                            </Headline>
                            <div>{t("form.ticket.subheadline")}</div>
                        </legend>
                        <FormField
                            type="text"
                            name="email"
                            label={t("form.ticket.email.label")}
                            placeholder={t("form.ticket.email.label")}
                            required
                        />
                        <FormField
                            type="select"
                            name="salutation"
                            label={t("form.ticket.salutation.label")}
                            placeholder={t("form.ticket.salutation.label")}
                            options={[
                                t("form.ticket.salutation.value-01"),
                                t("form.ticket.salutation.value-02"),
                                t("form.ticket.salutation.value-03"),
                                ...t("form.ticket.salutation.value-04").split(
                                    ","
                                )
                            ]}
                            required
                        />
                        <FormField
                            type="text"
                            name="title"
                            label={t("form.ticket.title.label")}
                            placeholder={t("form.ticket.title.label")}
                        />
                        <FormField
                            type="text"
                            name="firstname"
                            label={t("form.ticket.firstname.label")}
                            placeholder={t("form.ticket.firstname.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="lastname"
                            label={t("form.ticket.surname.label")}
                            placeholder={t("form.ticket.surname.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="company"
                            label={t("form.ticket.company.label")}
                            placeholder={t("form.ticket.company.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="country"
                            label={t("form.ticket.country.label")}
                            placeholder={t("form.ticket.country.label")}
                        />
                        <FormField
                            type="text"
                            name="zip"
                            label={t("form.ticket.zipcode.label")}
                            placeholder={t("form.ticket.zipcode.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="city"
                            label={t("form.ticket.city.label")}
                            placeholder={t("form.ticket.city.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="street"
                            label={t("form.ticket.street.label")}
                            placeholder={t("form.ticket.street.label")}
                        />
                        <FormField
                            type="text"
                            name="number"
                            label={t("form.ticket.housenumber.label")}
                            placeholder={t("form.ticket.housenumber.label")}
                        />
                        <FormField
                            type="text"
                            name="phone"
                            label={t("form.ticket.phone.label")}
                            placeholder={t("form.ticket.phone.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="cardnumber"
                            label={t("form.ticket.cardnumber.label")}
                            placeholder={t("form.ticket.cardnumber.label")}
                            required
                        />
                        <FormField
                            type="textarea"
                            name="message"
                            label={t("form.ticket.yourmessage.label")}
                            placeholder={t("form.ticket.yourmessage.label")}
                        />
                        <FormField
                            type="checkbox"
                            name="newsletter"
                            label={t("form.ticket.newsletter.checkbox")}
                            placeholder={t("form.ticket.newsletter.checkbox")}
                        />
                        <FormField
                            type="checkbox"
                            name="gdpr"
                            label={t("form.ticket.privacypolicy.checkbox")}
                            placeholder={t(
                                "form.ticket.privacypolicy.checkbox"
                            )}
                        />
                        <div>{t("form.ticket.subtitle")}</div>
                        <FocusError />
                    </Form>
                </Formik>
            )}
            {isSent && <FormSent />}
        </>
    );
}

export { TicketOrderForm };
